import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ImagePlus, FileUp, Loader2, Info } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CATEGORY_LABELS } from "./BazaarListingCard";

// "Sell an Item" composer — the digital good itself (`file_url`) is uploaded here and NEVER sent
// back down in the public browse listing response; only a buyer's own paid order reveals it.
export function BazaarListingModal({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("map");
  const [price, setPrice] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [posting, setPosting] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setTitle(""); setDescription(""); setCategory("map"); setPrice("");
    setImageUrl(null); setFileUrl(null); setFileName(""); setIsAdult(false); setIsCustom(false);
  };

  const uploadTo = async (file, kind, setUrl, setBusy, extra) => {
    const formData = new FormData();
    formData.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/uploads?kind=${kind}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.url);
      if (extra) extra(data);
    } catch {
      toast.error("Upload failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Give it a title");
    const cents = Math.round(parseFloat(price) * 100);
    if (!cents || cents < 100) return toast.error("Minimum price is $1.00");
    if (!fileUrl && !isCustom) return toast.error("Attach the file buyers will receive (or mark this as a custom commission)");
    setPosting(true);
    try {
      const { data } = await api.post("/bazaar/listings", { title, description, category, price_cents: cents, image_url: imageUrl, file_url: fileUrl, is_adult: isAdult, is_custom: isCustom });
      toast.success("Listed on the Bazaar!");
      onCreated(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create that listing");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#322216] border-white/10 text-[#f1e0c5]" data-testid="bazaar-listing-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Sell on the Bazaar</DialogTitle>
          <DialogDescription className="sr-only">Create a new listing for the Bazaar marketplace</DialogDescription>
        </DialogHeader>
        <Input data-testid="bazaar-listing-title-input" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-black/30 border-white/10" maxLength={100} />
        <Textarea data-testid="bazaar-listing-description-input" placeholder="Describe what buyers get…" value={description} onChange={(e) => setDescription(e.target.value)} className="bg-black/30 border-white/10 min-h-[80px]" maxLength={1000} />
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-1.5">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="bazaar-listing-category-select" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {category === "token" && (
              <Popover>
                <PopoverTrigger data-testid="bazaar-listing-token-animation-info-button" className="text-[#D4AF37]/70 hover:text-[#D4AF37] shrink-0">
                  <Info className="w-4 h-4" />
                </PopoverTrigger>
                <PopoverContent className="max-w-xs bg-[#322216] border-white/10 text-[#f1e0c5] text-xs leading-relaxed" data-testid="bazaar-listing-token-animation-info-content">
                  <p className="font-semibold text-[#D4AF37] mb-1">Selling an animated token?</p>
                  Build it with Call to Arms' Token Animation Maker (in any game's GM Assets tab), then export it as a GIF and upload that GIF right here as your preview image and/or file — it'll auto-play for buyers on the Bazaar.
                </PopoverContent>
              </Popover>
            )}
          </div>
          <Input data-testid="bazaar-listing-price-input" type="number" min="1" step="0.01" placeholder="Price (USD)" value={price} onChange={(e) => setPrice(e.target.value)} className="bg-black/30 border-white/10" />
        </div>

        <div className="flex gap-2">
          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" data-testid="bazaar-listing-image-input" onChange={(e) => e.target.files?.[0] && uploadTo(e.target.files[0], "bazaar_listing_image", setImageUrl, setUploadingImage)} />
          <Button type="button" variant="secondary" disabled={uploadingImage} className="rounded-full gap-2 flex-1" onClick={() => imageInputRef.current?.click()}>
            {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {imageUrl ? "Preview added" : "Add Preview Image"}
          </Button>
          <input ref={fileInputRef} type="file" className="hidden" data-testid="bazaar-listing-file-input" onChange={(e) => e.target.files?.[0] && uploadTo(e.target.files[0], "bazaar_listing_file", setFileUrl, setUploadingFile, () => setFileName(e.target.files[0].name))} />
          <Button type="button" variant="secondary" disabled={uploadingFile || isCustom} className="rounded-full gap-2 flex-1" onClick={() => fileInputRef.current?.click()}>
            {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            {fileUrl ? fileName || "File attached" : isCustom ? "Not needed for custom" : "Attach File"}
          </Button>
        </div>
        <p className="text-[10px] text-[#f1e0c5]/35">The preview image is public; the attached file only unlocks for buyers after they purchase.</p>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox data-testid="bazaar-listing-adult-checkbox" checked={isAdult} onCheckedChange={setIsAdult} />
          This item contains adult content (its preview image will be blurred on the Bazaar)
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox data-testid="bazaar-listing-custom-checkbox" checked={isCustom} onCheckedChange={(v) => { setIsCustom(v); if (v) setFileUrl(null); }} />
          This is a custom commission (made to order — buyers contact you for details before you deliver)
        </label>

        <Button data-testid="bazaar-listing-submit-button" onClick={submit} disabled={posting || uploadingImage || uploadingFile} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
          {posting ? "Listing…" : "List It"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
